<template>
    <div class="border border-solid border-black text-center p-5 pb-8 md:m-4 bg-white">
        <h2 v-text="$t('DELIVERIES_SUMMARY_TITLE')"/>
        <div class="my-4">
            <div class="m-2">
                <p v-if="selectedPlan" v-html="$t('DELIVERIES_SUMMARY_SHORT', {
                    deliveries: totalDeliveries,
                    frequency: frequency})"/>
                <p v-else v-text="$t('DELIVERIES_SUMMARY_YOU_TELL')"></p>
            </div>

            <div v-show="selectedPlan && !isPopMulti" class="flex items-center w-10/12 md:w-3/5 mx-auto my-6">
                <div class="w-1/6 md:w-2/6 flex items-center justify-center">
                    <img class="image-height" :class="{'pop-box': isPopMulti}" :src="getVaseImage" alt="vase">
                </div>
                <div class="w-5/6 md:w-4/6 pl-1">
                    <p v-if="!isPopMulti" v-html="$t(getVaseName)"/>
                </div>
            </div>

            <div v-show="selectedPlan" class="m-2" :class="{'pop-box': isPopMulti}">
                <p v-html="$t('DELIVERIES_SUMMARY_FULL', {
                    deliveries: totalDeliveries,
                    price: multipleDeliveriesPrice,
                    totalPrice: totalPrice})"/>
                <p v-if="(coupon && validDiscount && !isPopMulti) || (specialDatesCoupon)" v-html="$t('DELIVERIES_SUMMARY_DISCOUNTED', {discountedPrice: discountedPrice})"/>
            </div>
        </div>

        <h2 v-if="primaryProductsInGiftingEnable">{{$t('GIFTING_SWAP_BOX_SIZE')}}</h2>
        <div class="bumper-box" v-if="primaryProductsInGiftingEnable">
            <div class="bumper-box--select">
                <radio-field
                    :label="$t('GIFTING_POP_MULTI')"
                    :subLable="$t('GIFTING_POP_STEMS')"
                    :customStyle="customStyle"
                    :option="popBoxOption"
                    v-if="isPopMultiEnabled"
                    :price="subscriptionPopPrice"
                    v-model="boxType"/>
                <radio-field
                    :label="$t('GIFTING_STANDARD_MULTI')"
                    :subLable="$t('GIFTING_STANDARD_STEMS')"
                    :customStyle="customStyle"
                    :option="standardBoxOption"
                    :price="weeklySubscriptionPrice"
                    v-model="boxType"/>
                <radio-field
                    :label="$t('GIFTING_BUMPER_MULTI', {theBox: bumperBoxName })"
                    :subLable="$t('GIFTING_BUMPER_STEMS')"
                    :customStyle="customStyle"
                    :option="bumperBoxOption"
                    :price="subscriptionBumperPrice"
                    v-model="boxType"/>
            </div>
        </div>

        <div class="sm:px-20 md:px-0 lg:px-16 xl:px-32">
            <button class="btn btn--primary-black btn--full-width"
                    @click="submit"
                    v-html="$t('DELIVERIES_SUMMARY_NEXT')">
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { PLAN_FORTNIGHTLY,
    PLAN_MONTHLY,
    PLAN_WEEKLY,
    SECTION_NUMBER_OF_DELIVERIES,
    PRODUCT_TYPE_MULTI,
    PRODUCT_FAMILY_BUMPER,
    PRODUCT_TYPE_BUMPER_MULTI,
    DISPLAY_NAME_BUMPER, PRODUCT_TYPE_POP_MULTI } from '@/constants/Gifting';
import RadioField from '@/components/forms/fields/RadioField';
import { CSPageEvents } from '@/helpers/GAHelpers';

export default {
    name: 'DeliveriesSummary',
    components: { RadioField },
    data() {
        return {
            boxType: null,
            bumperBoxInfoModal: false,
            bumperBoxName: DISPLAY_NAME_BUMPER,
        };
    },
    methods: {
        submit() {
            CSPageEvents('giftOneOff', {
                productType: 'gift',
                productCategory: 'subscriptionGift',
                productFamily: this.selectedBoxName || this.defaultProductFamily,
                frequency: 'once',
            });
            CSPageEvents(undefined, { productName: `${this.numberOfMonthsForSubscription}Months` });
            this.$store.commit('gifting/setSectionComplete', SECTION_NUMBER_OF_DELIVERIES);
            this.$router.push({ name: 'account.gifting.delivery-address' });
        },
        couponDiscountValue(coupon) {
            if (coupon?.discountType === 'percentage') {
                return this.totalPrice * (coupon?.discount / 100);
            } else if (coupon?.discountType === 'amount') {
                return coupon?.discount * 0.01;
            }
            return 0;
        },
        calculateDiscountAmount() {
            let coupon = this.coupon || null;
            if (this.specialDatesCoupon && this.isPopMulti) {
                coupon = this.specialDatesCoupon;
            }
            if (this.specialDatesEnabled && this.specialDatesCoupon && (this.couponDiscountValue(coupon) <= this.couponDiscountValue(this.specialDatesCoupon))) {
                coupon = this.specialDatesCoupon;
            }
            if ((this.specialDatesCoupon && this.isPopMulti) || !this.isPopMulti) {
                const discountType = coupon.discountType;
                const discountValue = coupon.discount;

                if (discountType === 'percentage') {
                    const saving = this.totalPrice * (discountValue / 100);
                    return saving;
                } else if (discountType === 'amount') {
                    const saving = discountValue * 0.01;
                    return saving;
                }
            }
            return 0;
        },
    },
    mounted() {
        this.$store.dispatch('gifting/fetchPrices');
        this.$store.dispatch('gifting/fetchGiftingCoupons');
        this.boxType = this.primaryProductsInGiftingEnable ? this.selectedBoxName || PRODUCT_TYPE_MULTI : PRODUCT_TYPE_MULTI;
    },
    computed: {
        ...mapGetters({
            selectedPlan: 'gifting/getFrequencyForSubscription',
            totalDeliveries: 'gifting/getNumberOfDeliveriesForSubscription',
            totalPrice: 'gifting/getTotalPriceForDeliveries',
            coupon: 'gifting/getMultiGiftCoupon',
            selectedBoxName: 'gifting/getSelectedBoxName',
            primaryProductsInGiftingEnable: 'account/getPrimaryProductInGiftingEnabled',
            specialDatesCoupon: 'gifting/getSpecialDatesGiftCoupon',
            specialDatesEnabled: 'account/getSpecialDatesEnabled',
            numberOfMonthsForSubscription: 'gifting/getNumberOfMonthsForSubscription',
            isPopMultiEnabled: 'account/getPopMultiGift',
            currency: 'localisation/getCurrency',
            defaultProductFamily: 'register/getDefaultProductFamily',
        }),
        frequency() {
            switch (this.selectedPlan) {
            case PLAN_WEEKLY:
                return this.$t('FREQUENCY_SELECTION_WEEKLY_NOUN');
            case PLAN_FORTNIGHTLY:
                return this.$t('FREQUENCY_SELECTION_FORTNIGHTLY_NOUN');
            case PLAN_MONTHLY:
                return this.$t('FREQUENCY_SELECTION_MONTHLY_NOUN');
            }
        },
        standardBoxOption() {
            return PRODUCT_TYPE_MULTI;
        },
        bumperBoxOption() {
            return PRODUCT_TYPE_BUMPER_MULTI;
        },
        popBoxOption() {
            return PRODUCT_TYPE_POP_MULTI;
        },
        multipleDeliveriesPrice() {
            return parseInt(this.$store.getters['gifting/getProductTypePriceFromName'](this.selectedBoxName));
        },
        weeklySubscriptionPrice() {
            return this.$store.getters['gifting/getProductTypePriceFromName'](PRODUCT_TYPE_MULTI);
        },
        subscriptionBumperPrice() {
            return this.$store.getters['gifting/getProductTypePriceFromName'](PRODUCT_TYPE_BUMPER_MULTI);
        },
        subscriptionPopPrice() {
            return this.$store.getters['gifting/getProductTypePriceFromName'](PRODUCT_TYPE_POP_MULTI);
        },
        discountedPrice() {
            const saving = this.calculateDiscountAmount();
            const price = this.totalPrice - saving;
            return price.toFixed(2);
        },
        validDiscount() {
            if ((this.selectedBoxName === PRODUCT_TYPE_BUMPER_MULTI && !this.coupon?.product_families.includes(PRODUCT_FAMILY_BUMPER)) ||
                (this.selectedBoxName === PRODUCT_TYPE_MULTI && !this.coupon?.product_families.includes(PRODUCT_FAMILY_BUMPER))) {
                return false;
            }
            const minBoxes = this.coupon?.minimumWeeks;
            return this.totalDeliveries >= minBoxes;
        },
        getVaseImage() {
            return require(`@/assets/images/pages/account/gifting/vase-${this.boxType === PRODUCT_TYPE_BUMPER_MULTI ? 'bumper.png' : 'small.svg'}`);
        },
        getVaseName() {
            return this.boxType === PRODUCT_TYPE_BUMPER_MULTI ? 'DELIVERIES_SUMMARY_HURRICANE_VASE_COPY' : 'DELIVERIES_SUMMARY_CLASSIC_VASE_COPY';
        },
        isPopMulti() {
            return this.selectedBoxName === PRODUCT_TYPE_POP_MULTI;
        },
        customStyle() {
            return {
                'justify-content': 'space-between',
            };
        },
    },
    watch: {
        boxType: function(newVal) {
            this.$store.commit('gifting/setSelectedBoxName', newVal);
        },
    },
};
</script>

<style lang="scss" scoped>
.bumper-box {
    padding: 0 20px;
    &--select {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        margin-bottom: 56px;
    }
}
.pop-box {
    padding-top: -10px;
}
.image-height {
    height: 4rem;
}
@media (min-width: 768px) {
    .image-height {
        height: 6rem;
    }
}
</style>
